import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { clsx } from 'clsx';
import { removePatternFormat } from 'react-number-format';
import type { ChangeMeta } from 'react-number-format';

import type { WidgetMessages } from '../../../../types/widgetMessages';
import { getFallbackChangeMeta, getPhoneDigits, getPhoneLengthError, stripExtraPhoneDigits } from '../utils';

import type { Country } from '../../model';
import { BACKDROP_FADE_DISTANCE, COUNTRIES, DEFAULT_COUNTRY } from '../constants';

import styles from '../../styles.module.css';

interface UseInputPhoneParams {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  phoneMessages: WidgetMessages['phone'];
  error?: string;
  selectedCountry?: Country;
  setSelectedCountry?: (country: Country) => void;
}

export const useInputPhone = ({
  value,
  onChange,
  error,
  disabled,
  selectedCountry,
  setSelectedCountry,
  phoneMessages,
}: UseInputPhoneParams) => {
  const [localSelectedCountry, setLocalSelectedCountry] = useState<Country>(
    selectedCountry || COUNTRIES.find((c) => c.code === DEFAULT_COUNTRY) || COUNTRIES[0]
  );

  const currentSelectedCountry = selectedCountry || localSelectedCountry;
  const updateSelectedCountry = setSelectedCountry || setLocalSelectedCountry;
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [currentDragY, setCurrentDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (selectedCountry && selectedCountry.code !== localSelectedCountry.code) {
      setLocalSelectedCountry(selectedCountry);
    }
  }, [selectedCountry, localSelectedCountry.code]);

  const handleCountryChange = useCallback(
    (code: string) => {
      const country = COUNTRIES.find((c) => c.code === code);
      if (country) {
        updateSelectedCountry(country);
        onChangeRef.current('');
      }
      setIsSelectOpen(false);
    },
    [updateSelectedCountry]
  );

  const handleValueChange = useCallback((values: { value: string }) => {
    if (values.value !== valueRef.current) {
      onChangeRef.current(values.value);
    }
  }, []);

  const handleRemoveFormatting = useCallback(
    (inputValue: string, changeMeta?: ChangeMeta) => {
      const raw = removePatternFormat(inputValue, changeMeta ?? getFallbackChangeMeta(inputValue), {
        format: currentSelectedCountry.format,
        patternChar: '#',
      });

      return stripExtraPhoneDigits(raw, currentSelectedCountry);
    },
    [currentSelectedCountry]
  );

  let validationError: string | undefined = undefined;
  if (value) {
    const lengthError = getPhoneLengthError(getPhoneDigits(value).length, currentSelectedCountry);
    if (lengthError === 'minLength') {
      validationError = phoneMessages.minLength(currentSelectedCountry.minLength);
    } else if (lengthError === 'invalidFormat') {
      validationError = phoneMessages.invalidFormat;
    }
  }

  const handleSelectClick = useCallback(() => {
    if (!disabled) {
      setIsSelectOpen((prev) => !prev);
    }
  }, [disabled]);

  // Обработчики свайпа для мобильной версии
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (panelRef.current) {
      setDragStartY(e.touches[0].clientY);
      setCurrentDragY(0);
      setIsDragging(true);
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (isDragging && panelRef.current) {
        const deltaY = e.touches[0].clientY - dragStartY;
        if (deltaY > 0) {
          setCurrentDragY(deltaY);
          e.preventDefault();
        }
      }
    },
    [isDragging, dragStartY]
  );

  const handleTouchEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      if (currentDragY > 100) {
        setIsSelectOpen(false);
      }
      setCurrentDragY(0);
      setDragStartY(0);
    }
  }, [isDragging, currentDragY]);

  useEffect(() => {
    if (!isSelectOpen) {
      return;
    }

    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isSelectOpen]);

  useEffect(() => {
    if (!isSelectOpen) {
      setCurrentDragY(0);
      setIsDragging(false);
    }
  }, [isSelectOpen]);

  const hasError = useMemo(() => !!(error || validationError), [error, validationError]);

  const panelTransform = useMemo(
    () => (isDragging && currentDragY > 0 ? { transform: `translateY(${currentDragY}px)` } : {}),
    [isDragging, currentDragY]
  );

  const backdropStyle = useMemo(() => {
    if (isDragging && currentDragY > 0) {
      return { opacity: 1 - currentDragY / BACKDROP_FADE_DISTANCE };
    }
    return { opacity: 1 };
  }, [isDragging, currentDragY]);

  const panelClassName = useMemo(
    () => clsx(styles.countrySelectPanel, isDragging && styles.countrySelectPanelDragging),
    [isDragging]
  );

  const inputCombinedClassName = useMemo(
    () => clsx(styles.phoneInputCombined, hasError && styles.phoneInputCombinedError),
    [hasError]
  );

  const chevronClassName = useMemo(() => (isSelectOpen ? styles.chevronUp : styles.chevronDown), [isSelectOpen]);

  const getOptionClassName = useCallback(
    (country: Country) => {
      return clsx(styles.countryOption, currentSelectedCountry.code === country.code && styles.countryOptionSelected);
    },
    [currentSelectedCountry]
  );

  return {
    selectedCountry: currentSelectedCountry,
    isSelectOpen,
    validationError,
    panelTransform,
    backdropStyle,
    panelClassName,
    inputCombinedClassName,
    chevronClassName,
    getOptionClassName,
    wrapperRef,
    panelRef,
    handleCountryChange,
    handleValueChange,
    handleRemoveFormatting,
    handleSelectClick,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
